import { DAY_OF_MONTH_FORMAT } from '@beeline.com/bp-design-system';
import {
  getMetaData,
  getNotificationState,
  KnownMetadataKeys
} from 'common/header/notifications/hooks/mappers/impl/shared';
import {
  type MappedNotification,
  type NotificationMapper,
  type NotificationMapperOptions
} from 'common/header/notifications/Notifications.types';
import { capitalizeOnlyFirstLetter } from 'utils/textUtil';
import { getGenericImportUrl } from './shared/getGenericImportUrl';

export const importMapper: NotificationMapper = (notification, options) => {
  const { isReady, isError } = getNotificationState(notification.status);
  return {
    ...notification,
    title: getImportTitle(notification, options).title,
    to: isReady || isError ? systemImportUrl(notification, options) : undefined
  };
};

function systemImportUrl(notification: MappedNotification, options: NotificationMapperOptions) {
  return isInvoiceImportNotification(notification)
    ? `/${options.tenantCode}/settings/monitoring/invoiceimports`
    : getGenericImportUrl(notification, options.tenantCode);
}

export function isInvoiceImportNotification(notification: MappedNotification) {
  return importResourceName(notification) === 'INVOICE';
}

export function getImportTitle(notification: MappedNotification, options: NotificationMapperOptions) {
  return {
    title: getTitle(notification, options),
    description: getDescription(notification, options)
  };
}

function getTitle(notification: MappedNotification, options: NotificationMapperOptions): string {
  const { isReady, isError, isQueued, inProgress } = getNotificationState(notification.status);
  const resourceName = importResourceTitle(notification) ?? '';
  if (isReady) {
    return options.formatNotificationMessage('importTitleSuccess', { resourceName });
  }
  if (isError) {
    return options.formatNotificationMessage('importTitleError', { resourceName });
  }
  if (isQueued) {
    return options.formatNotificationMessage('importTitleQueued', { resourceName });
  }
  return inProgress ? options.formatNotificationMessage('importTitleInProgress', { resourceName }) : '';
}

function getDescription(notification: MappedNotification, options: NotificationMapperOptions): string {
  const state = getNotificationState(notification.status);
  if (state.isReady || state.isError) {
    return getImportUCICompletedDescription(notification, options);
  }
  if (state.isQueued) {
    return getImportUCIQueuedDescription(options);
  }
  return state.inProgress ? getImportUCIInProgressDescription(notification, options) : '';
}

function importResourceTitle(notification: MappedNotification) {
  return capitalize(importResourceName(notification));
}

function capitalize(str: string | undefined) {
  if (!str) {
    return str;
  }
  return str.split('_').map(capitalizeOnlyFirstLetter).join(' ');
}

function importResourceName(notification: MappedNotification) {
  return getImportEntityType(notification) ?? '';
}

const getImportUCICompletedDescription = (
  notification: MappedNotification,
  options: NotificationMapperOptions
): string => {
  const metadata = getMetaData(notification);
  return options.formatNotificationMessage('notifications.importsUCICompleted', {
    totalRecords: metadata.value('PROCESSED_RECORD_COUNT'),
    recordsWithError: metadata.value('FAILED_RECORD_COUNT'),
    type: getImportEntityType(notification),
    startDate: getImporterDate(notification, 'IMPORTER_STARTED_AT', options),
    endDate: getImporterDate(notification, 'IMPORTER_FINISHED_AT', options)
  });
};

function getImportEntityType(notification: MappedNotification) {
  const result = getMetaData(notification).value('IMPORTER_ENTITY_TYPE');
  return result === 'CUSTOM_OBJECTS' ? 'FINANCIAL_FIELD_VALUES' : result;
}

function getImportUCIInProgressDescription(
  notification: MappedNotification,
  options: NotificationMapperOptions
): string {
  const metadata = getMetaData(notification);

  return options.formatNotificationMessage('notifications.importsUCIInProgress', {
    type: metadata.value('IMPORTER_ENTITY_TYPE'),
    startDate: options.formatDateTime(metadata.value('IMPORTER_STARTED_AT'), {
      utc: true,
      numberMonth: false,
      format: {
        DAY_OF_MONTH: DAY_OF_MONTH_FORMAT.DIGIT
      },
      display: ['MONTH', 'DAY_OF_MONTH', 'DAY_OF_WEEK', 'TIME', 'TIMEZONE']
    })
  });
}

function getImportUCIQueuedDescription(options: NotificationMapperOptions): string {
  return options.formatNotificationMessage('notifications.importsUCIQueued');
}

function getImporterDate(
  notification: MappedNotification,
  key: Extract<KnownMetadataKeys, 'IMPORTER_FINISHED_AT' | 'IMPORTER_STARTED_AT'>,
  options: NotificationMapperOptions
) {
  const value = getMetaData(notification).value(key);
  if (value?.length && value !== 'null')
    return options.formatDateTime(value, {
      utc: true,
      numberMonth: false,
      format: {
        DAY_OF_MONTH: DAY_OF_MONTH_FORMAT.DIGIT
      },
      display: ['MONTH', 'DAY_OF_MONTH', 'DAY_OF_WEEK', 'TIME', 'TIMEZONE']
    });
  return ' - ';
}
